"use strict";
/**
 * Agaru design tokens — the one source for color, material, type and shape
 * across every channel: web dashboard, TV, mobile app and watch companions.
 *
 * The palette is Sadu: the Bedouin tent weave. Surfaces are indigo night
 * (the dyed ground of the weave), text is undyed wool, and the one action
 * color is madder red. Elevation is a lighter indigo plus a 0.5–1px wool
 * hairline instead of drop shadows. Color is reserved for meaning: madder for
 * actions, the zone matrix for effort, and system semantics for status.
 *
 * Plain values only (no platform imports) so Tailwind configs, React Native
 * styles and the native watch apps (which mirror these by hand — see
 * docs/brand.md) all read the same numbers.
 *
 * Contrast notes are WCAG ratios on carbon.base (#0E1022) / carbon.elevated (#191C33).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.tokens = exports.font = exports.material = exports.touchTarget = exports.space = exports.radius = exports.ring = exports.zoneColors = exports.zoneUnknown = exports.zones = exports.semantic = exports.camel = exports.brand = exports.label = exports.hairline = exports.carbon = void 0;
exports.zoneColor = zoneColor;
exports.carbon = {
    /** Indigo night: the page / screen ground on web, TV and phone. */
    base: '#0E1022',
    /** Elevated card. */
    elevated: '#191C33',
    /** Card on card, inputs, pressed rows. */
    elevated2: '#252945',
    /** Hover / selected fills, track backgrounds. */
    elevated3: '#31365A',
    /** Translucent card fill for surfaces over moving content (with blur). */
    translucent: 'rgba(25, 28, 51, 0.72)',
    /** Header / tab-bar material over scrolling content (with blur). */
    chrome: 'rgba(14, 16, 34, 0.72)',
    /** Watch faces stay true black: OLED power and watchOS/Wear convention. */
    watch: '#000000',
};
exports.hairline = {
    /** Default edge (inset border) on indigo surfaces: a wool thread. */
    edge: 'rgba(241, 238, 232, 0.12)',
    /** Stronger edge for focused / selected surfaces. */
    edgeStrong: 'rgba(241, 238, 232, 0.20)',
    /** Top-edge highlight: light catching the material. */
    specular: 'rgba(241, 238, 232, 0.08)',
    /** Row separators inside a card. */
    separator: 'rgba(241, 238, 232, 0.08)',
    /** Physical widths: 0.5px on retina, 1px fallback. */
    widthHair: 0.5,
    width: 1,
};
exports.label = {
    /** Undyed wool. Primary text — 14.5:1 on elevated. */
    primary: '#F1EEE8',
    /** Secondary text — 8.1:1 on elevated. */
    secondary: '#B4B3C2',
    /** Tertiary / captions — 5.5:1 on elevated. */
    tertiary: '#9291A8',
    /** Disabled / placeholder only (3.1:1 on elevated — never body copy). */
    quaternary: '#66678A',
};
/**
 * Madder red, the dye of the Sadu weave. Actions, focus, your own row.
 * 500/600/700 are fills (white text on 500 is 5.1:1, on 600 6.0:1); they are
 * not text colors on indigo. Brand-colored text and icons use 300 (6.6:1).
 */
exports.brand = {
    300: '#FF7A6E',
    500: '#C93A30',
    600: '#B8312A',
    700: '#A12A24',
    /** Text on a madder fill. */
    on: '#FFFFFF',
};
/** Camel: the undyed hair stripe. Secondary accent (badges, highlights) — 8.8:1. */
exports.camel = '#D9B68A';
exports.semantic = {
    success: '#30D158',
    warning: '#FFD60A',
    danger: '#FF453A',
    /** Destructive fill under white text (5.4:1); `danger` is for text/icons on indigo.
     *  Brand is red too, so destructive controls are outlined with an icon, never a bare red fill. */
    dangerFill: '#D70015',
    info: '#64D2FF',
};
/**
 * The metabolic matrix: one ordered thermal sweep from cool recovery to
 * flame, identical on TV, web, phone and watch. Every value clears 5:1 on
 * indigo cards and on black, so the same color works for rings, bars and
 * digits. Z5 leans magenta so max effort never reads as the madder brand. Color is never the only cue: always pair it with the zone number.
 */
exports.zones = [
    { key: 'ZONE_1', short: 'Z1', name: 'Recovery', color: '#8E9AAF' },
    { key: 'ZONE_2', short: 'Z2', name: 'Endurance', color: '#3CC8D0' },
    { key: 'ZONE_3', short: 'Z3', name: 'Tempo', color: '#8BD86A' },
    { key: 'ZONE_4', short: 'Z4', name: 'Threshold', color: '#FF9640' },
    { key: 'ZONE_5', short: 'Z5', name: 'Max', color: '#FF5C9A' },
];
/** Shown before the first heart-rate reading. */
exports.zoneUnknown = exports.label.quaternary;
/** zones[] colors in order — convenient for interpolation ranges. */
exports.zoneColors = exports.zones.map((z) => z.color);
function zoneColor(zone) {
    return exports.zones.find((z) => z.key === zone)?.color ?? exports.zoneUnknown;
}
/** Ring / progress tracks: goal rings use madder over this track. */
exports.ring = {
    track: 'rgba(241, 238, 232, 0.10)',
    goal: exports.brand[500],
    lineWidth: { compact: 6, regular: 10, hero: 14 },
};
exports.radius = {
    control: 12,
    card: 20,
    sheet: 28,
    pill: 9999,
};
/** 4px grid. Touch targets never go under 44pt (iOS) / 48dp (Android). */
exports.space = { 1: 4, 2: 8, 3: 12, 4: 16, 5: 20, 6: 24, 8: 32, 10: 40, 12: 48 };
exports.touchTarget = { ios: 44, android: 48, watch: 44 };
exports.material = {
    /** backdrop-filter for translucent chrome and cards. */
    blur: 20,
    saturate: 1.8,
};
exports.font = {
    /** System first: SF on Apple platforms, then Inter / platform sans. */
    web: '-apple-system, BlinkMacSystemFont, "SF Pro Text", Inter, system-ui, sans-serif',
    webDisplay: '-apple-system, BlinkMacSystemFont, "SF Pro Display", Inter, system-ui, sans-serif',
    /** Every telemetry number, timer and counter. */
    numericVariant: 'tabular-nums',
};
exports.tokens = {
    carbon: exports.carbon,
    hairline: exports.hairline,
    label: exports.label,
    brand: exports.brand,
    camel: exports.camel,
    semantic: exports.semantic,
    zones: exports.zones,
    zoneUnknown: exports.zoneUnknown,
    ring: exports.ring,
    radius: exports.radius,
    space: exports.space,
    touchTarget: exports.touchTarget,
    material: exports.material,
    font: exports.font,
};
//# sourceMappingURL=tokens.config.js.map